import { z } from "zod";

import {
  BlockAlignment,
  BlockLayout,
  BlockType,
  DimensionsMode,
  HeadingLevel,
  NavigationPosition,
  TabType,
  TabVisibility,
  TileActionType,
  TileImageType,
  WelcomePageType,
} from "@/enums/bpCustomisation";

import type { Component } from "vue";

const welcomePageTypeSchema = z.enum(Object.values(WelcomePageType));
const navigationPositionSchema = z.enum(Object.values(NavigationPosition));
const tabVisibilitySchema = z.enum(Object.values(TabVisibility));
const blockTypeSchema = z.enum(Object.values(BlockType));
const blockAlignmentSchema = z.enum(Object.values(BlockAlignment));
const blockLayoutSchema = z.enum(Object.values(BlockLayout));
const headingLevelSchema = z.enum(Object.values(HeadingLevel));
const tileActionTypeSchema = z.enum(Object.values(TileActionType));
const tileImageTypeSchema = z.enum(Object.values(TileImageType));

const dimensionsSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal(DimensionsMode.SCALE),
    scale: z.number().nullable().optional(),
  }),
  z.object({
    mode: z.literal(DimensionsMode.FULL_WIDTH),
  }),
]);

const templateClient = z.object({
  name: z.string(),
  id: z.string(),
});

const tileImageSchema = z.object({
  type: tileImageTypeSchema,
  icon: z.string().optional(),
  file_id: z.number().optional().nullable(),
  dimensions: dimensionsSchema.optional(),
});

const tileActionSchema = z.object({
  type: tileActionTypeSchema,
  text: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
});

const tileSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  action: tileActionSchema,
  image: tileImageSchema,
});

const blockSchema = z.object({
  id: z.string().optional(),
  type: blockTypeSchema,
  icon: z.string().optional(),
  align: blockAlignmentSchema.optional(),
  file_id: z.number().optional().nullable(),
  text: z.string().nullable().optional(),
  url: z.string().optional(),
  file_url: z.string().optional(),
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  layout: blockLayoutSchema.optional(),
  heading: headingLevelSchema.optional(),
  tiles: z.array(tileSchema).optional(),
  dimensions: dimensionsSchema.optional(),
});

const baseTabSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  icon: z.string().nullable(),
  navigation_position: navigationPositionSchema.nullable(),
  visibility: tabVisibilitySchema.nullable(),
});

const tabSchema = z.discriminatedUnion("type", [
  baseTabSchema.extend({
    type: z.literal(TabType.WELCOME_PAGE),
    mode: welcomePageTypeSchema,
    agent_id: z.string().optional(),
    show_header: z.boolean().optional(),
    show_login_button: z.boolean().optional(),
    start_message_title: z.string().nullable().optional(),
    start_message_text: z.string().nullable().optional(),
  }),

  baseTabSchema.extend({
    type: z.literal(TabType.WORKFLOW),
    workflow_template_ids: z.array(z.string()).optional(),
    is_fallback: z.boolean().optional(),
  }),

  baseTabSchema.extend({
    type: z.literal(TabType.CUSTOM),
    blocks: z.array(blockSchema).optional(),
  }),

  baseTabSchema.extend({
    type: z.literal(TabType.DOCUMENTS),
  }),

  baseTabSchema.extend({
    type: z.literal(TabType.PROFILE),
  }),

  baseTabSchema.extend({
    type: z.literal(TabType.FAQ),
  }),

  baseTabSchema.extend({
    type: z.literal(TabType.LOG_OUT),
  }),
]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const welcomePageSchema = z.object({
  type: welcomePageTypeSchema,
  agent_id: z.string().optional(),
  show_header: z.boolean().optional(),
  show_login_button: z.boolean().optional(),
  start_message_title: z.string().nullable().optional(),
  start_message_text: z.string().nullable().optional(),
});

const borrowerPlatformTemplateContentSchema = z.object({
  version: z.string(),
  tabs: z.array(tabSchema),
  is_footer_enabled: z.boolean().optional(),
  footer: z.string().nullable().optional(),
});

export const borrowerPlatformTemplateListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  client: z.any().nullable(),
  is_active: z.boolean(),
  is_global: z.boolean(),
  is_fallback: z.boolean().nullable(),
  is_lendflow_template: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const borrowerPlatformTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  content: borrowerPlatformTemplateContentSchema,
  is_active: z.boolean(),
  is_global: z.boolean(),
  created_at: z.string(),
  client: templateClient.optional().nullable(),
  updated_at: z.string(),
});

export const createBorrowerPlatformTemplateRequestSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  content: borrowerPlatformTemplateContentSchema,
  is_active: z.boolean(),
});

export const updateBorrowerPlatformTemplateRequestSchema =
  createBorrowerPlatformTemplateRequestSchema.partial().extend({
    id: z.string(),
  });

export const borrowerPlatformTemplateFiltersSchema = z.object({
  search: z.string().optional(),
  page: z.number().optional(),
  per_page: z.number().optional(),
  sort_by: z.string().optional(),
  client_id: z.string().optional(),
  only_global: z.number().optional(),
});

export type BorrowerPlatformTemplateListItem = z.infer<
  typeof borrowerPlatformTemplateListItemSchema
>;
export type BorrowerPlatformTemplate = z.infer<
  typeof borrowerPlatformTemplateSchema
>;
export type CreateBorrowerPlatformTemplateRequest = z.infer<
  typeof createBorrowerPlatformTemplateRequestSchema
>;
export type UpdateBorrowerPlatformTemplateRequest = z.infer<
  typeof updateBorrowerPlatformTemplateRequestSchema
>;
export type BorrowerPlatformTemplateFilters = z.infer<
  typeof borrowerPlatformTemplateFiltersSchema
>;
export type BorrowerPlatformTemplateContent = z.infer<
  typeof borrowerPlatformTemplateContentSchema
>;
export type WelcomePage = z.infer<typeof welcomePageSchema>;
export type Tab = z.infer<typeof tabSchema>;

export type WelcomePageTab = Extract<Tab, { type: "welcome_page" }>;
export type Block = z.infer<typeof blockSchema>;
export interface Footer {
  is_footer_enabled?: boolean;
  footer?: string | null;
}
export type Tile = z.infer<typeof tileSchema>;
export type TileAction = z.infer<typeof tileActionSchema>;
export type TileImage = z.infer<typeof tileImageSchema>;
export type Dimensions = z.infer<typeof dimensionsSchema>;

export interface BorrowerPlatformTemplateActionMenuProps {
  template: BorrowerPlatformTemplateListItem;
  onEdit?: (template: BorrowerPlatformTemplateListItem) => void;
  onDuplicate?: (template: BorrowerPlatformTemplateListItem) => void;
  onDelete?: (template: BorrowerPlatformTemplateListItem) => void;
  onToggleGlobal?: (template: BorrowerPlatformTemplateListItem) => void;
}

export interface BorrowerPlatformTemplateFile {
  id: string;
  file_url: string;
}

export interface BlockWithIcon {
  type: BlockType;
  icon: string;
  iconComponent: Component;
  labelKey: string;
}

export interface BaseBlockProps {
  modelValue: Block;
}

export interface ImageBlockProps extends BaseBlockProps {
  templateId: string;
}

export interface TilesBlockProps extends BaseBlockProps {
  templateId?: string;
}
export {
  WelcomePageType,
  TabType,
  NavigationPosition,
  TabVisibility,
  BlockType,
  BlockAlignment,
  BlockLayout,
  HeadingLevel,
  TileActionType,
  TileImageType,
  DimensionsMode,
};
